import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  CombinedReturnItem,
  NewReturnItemData,
} from "@redotech/redo-customer-portal-shared/flow/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  DraftReturn,
  Order as DraftReturnOrder,
} from "@redotech/redo-model/draft-return/draft-return";
import { getPrimaryProduct } from "@redotech/redo-model/draft-return/util";
import { getResource } from "@redotech/redo-model/localization/resource";
import { ReturnType } from "@redotech/redo-model/return";
import {
  InputAnswerPartWithStep,
  returnFlowJsonFormat,
} from "@redotech/redo-model/return-flow";
import { THIRD_PARTY_SOURCE } from "@redotech/redo-model/third-party";
import { getDateString } from "@redotech/redo-web/date-utils";
import { Fragment, memo, useMemo, useRef, useState } from "react";
import { Footer } from "./components/footer";
import * as footerStyles from "./components/footer.module.css";
import { ReturnItemCard, ReturnItemFlow } from "./components/return-item-flow";
import * as returnStyles from "./return.module.css";
import {
  cleanResponses,
  getWarrantyRegistrationData,
  useSubmitWarrantyRegistration,
} from "./use-submit-warranty-registration";

export const ChooseItems = memo(function ChooseItems({
  draftReturn,
  setDraftReturn,
  returnType,
  registerWarranty,
  settings,
  onComplete,
  notes,
  onReject,
  isRegistrationClaim = false,
}: {
  draftReturn: DraftReturn;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  onComplete?: (draftReturn: DraftReturn) => void;
  onReject?: () => void;
  returnType: ReturnType;
  registerWarranty: boolean;
  settings: ReturnAppSettings;
  notes?: InputAnswerPartWithStep[];
  isRegistrationClaim?: boolean;
}) {
  const client = useRequiredContext(RpcClientContext);

  const combinedItems: CombinedReturnItem[] = useMemo(() => {
    const combinedReturnItems: CombinedReturnItem[] = [];
    for (const returnableItem of draftReturn.returnableItems) {
      const associatedPendingItems = draftReturn.pendingReturnItems.filter(
        (pendingItem) => pendingItem.returnableItemId === returnableItem.id,
      );

      if (associatedPendingItems.length === 0) {
        const primaryProduct = getPrimaryProduct(returnableItem);
        combinedReturnItems.push({
          ...returnableItem,
          availableQuantity: primaryProduct?.quantity ?? 1,
        });
      } else {
        const returnQuantity = associatedPendingItems.reduce(
          (acc, pendingItem) => acc + (pendingItem.quantity ?? 1),
          0,
        );

        const availableQuantity =
          (getPrimaryProduct(returnableItem)?.quantity ?? 1) - returnQuantity;
        for (const pendingItem of associatedPendingItems) {
          combinedReturnItems.push({
            ...returnableItem,
            pendingItem,
            availableQuantity,
          });
        }

        if (availableQuantity > 0) {
          combinedReturnItems.push({ ...returnableItem, availableQuantity });
        }
      }
    }

    return combinedReturnItems;
  }, [draftReturn]);

  const [selectedItem, setSelectedItem] = useState<CombinedReturnItem | null>(
    combinedItems.length === 1 && registerWarranty ? combinedItems[0] : null,
  );

  const itemsReadyForReturn = useMemo(
    () => combinedItems.filter((combinedItem) => !!combinedItem.pendingItem),
    [combinedItems],
  );

  const addItemRef = useRef<NewReturnItemData | null>(null);
  const [addItemLoad, addItem] = useTriggerLoad(async (signal) => {
    setSelectedItem(null);
    if (!setDraftReturn) {
      return;
    }
    const {
      item,
      flow,
      flowPath,
      multipleChoiceAnswers,
      inputAnswers,
      variantExchangeItem,
      exchangeGroup,
      advancedExchangeItems,
      expandedProductVariantExchanges,
    } = addItemRef.current!;

    const allInputAnswers: InputAnswerPartWithStep[] = [...inputAnswers];
    if (notes) {
      allInputAnswers.push(...notes);
    }

    try {
      const request = {
        draftReturnId: draftReturn._id,
        previousPendingItemId: item.pendingItem?.id,
        pendingItem: {
          returnableItemId: item.id,
          flow: returnFlowJsonFormat.write(flow),
          flowPath,
          quantity: item?.pendingItem
            ? (item?.pendingItem?.quantity ?? 1)
            : item.availableQuantity,
          inputAnswers: allInputAnswers.map((answer) => ({
            ...answer,
            value: Array.isArray(answer.value)
              ? answer.value.map((url) => url.toString())
              : answer.value,
          })),
          multipleChoiceAnswers,
          variantExchangeItem,
          exchangeGroup,
          advancedExchangeItems,
          expandedProductVariantExchanges,
        },
      };

      const { draftReturn: updatedDraftReturn } = await client.addReturnItem(
        request,
        { signal },
      );
      setDraftReturn(updatedDraftReturn);
      if (
        updatedDraftReturn.pendingReturnItems.length === combinedItems.length &&
        !settings?.bulkReturnsEnabled
      ) {
        onComplete?.(updatedDraftReturn);
      }
    } catch (error) {
      console.error(error);
    } finally {
      addItemRef.current = null;
    }
  });

  const { submitWarrantyRegistration } = useSubmitWarrantyRegistration({
    setDraftReturn,
  });

  const addItemToReturn = useHandler(async (item: NewReturnItemData) => {
    if (registerWarranty && selectedItem) {
      const { responses, userInfo, optInMarketing, isRegisterOnly } =
        getWarrantyRegistrationData(item);

      // Only submit warranty registration during add item if they are also submitting a claim
      if (!isRegisterOnly) {
        await submitWarrantyRegistration({
          product: selectedItem.products[0],
          account: userInfo,
          responses: cleanResponses(responses),
          draftReturnId: draftReturn._id,
          notifyCustomer: true,
          optInMarketing,
        });
      }
    }

    addItemRef.current = item;
    addItem();
  });

  const cancelReturnItem = useHandler(async () => {
    setSelectedItem(null);
    if (selectedItem?.pendingItem && setDraftReturn) {
      const pendingItemId = selectedItem.pendingItem.id;
      const request = { draftReturnId: draftReturn._id, pendingItemId };
      const { draftReturn: updatedDraftReturn } =
        await client.removeReturnItem(request);
      setDraftReturn(updatedDraftReturn);
    }
  });

  const editReturnItem = useHandler(
    async (editedDraftReturn: DraftReturn, newItemId: string) => {
      const returnableItem = editedDraftReturn.returnableItems.find(
        (item) => item.id === newItemId,
      )!;
      setSelectedItem({
        ...returnableItem,
        availableQuantity: getPrimaryProduct(returnableItem)?.quantity ?? 1,
      });
      setDraftReturn?.(editedDraftReturn);
    },
  );

  const getOrderDate = (order: DraftReturnOrder) =>
    getDateString(order.orderDate);

  const primaryProduct = selectedItem?.products.find(
    (product) => product.id === selectedItem?.primaryProductId,
  );

  const [variantSelected, setVariantSelected] = useState<boolean | null>(null);

  useMemo(() => {
    if (
      primaryProduct?.variantData?.variants &&
      primaryProduct.variantData.variants.length > 1 &&
      isRegistrationClaim
    ) {
      setVariantSelected(true);
    }
  }, [primaryProduct, isRegistrationClaim, setVariantSelected]);

  const copyResource = getResource({
    variant: draftReturn.returnType,
    overrides: settings.resourceOverride,
  });

  return (
    <>
      <div className={returnStyles.page}>
        <div className={returnStyles.pagePadding}>
          <div>
            <div className={returnStyles.mainHeader}>
              {/* TODO: This should use the copy resource */}
              {registerWarranty
                ? "Fill out warranty details"
                : copyResource.CHOOSE_ITEMS_TEXT}
            </div>
          </div>
        </div>
        <div className={returnStyles.productCards}>
          {draftReturn.orders.map((order) => (
            <Fragment key={order.id}>
              {order.salesChannel !== THIRD_PARTY_SOURCE && (
                <div className={returnStyles.orderAndDate}>
                  <div className={returnStyles.header} key={order.name}>
                    <div className={returnStyles.flex}>
                      <div className={returnStyles.grayTextOnGrayBg}>
                        Order:&nbsp;
                      </div>
                      <div className={returnStyles.orderNumber}>
                        {order.name}
                      </div>
                    </div>
                    <div className={returnStyles.grayTextOnGrayBg}>
                      {getOrderDate(order)}
                    </div>
                  </div>
                </div>
              )}

              <>
                {combinedItems
                  .filter((item) => item.orderId === order.id)
                  .map((returnableItem) => (
                    <ReturnItemCard
                      addItemLoad={addItemLoad}
                      addItemRef={addItemRef}
                      client={client}
                      copyResource={copyResource}
                      draftReturn={draftReturn}
                      key={getKey(returnableItem)}
                      returnableItem={returnableItem}
                      selectedItem={selectedItem}
                      setDraftReturn={setDraftReturn}
                      setSelectedItem={setSelectedItem}
                      settings={settings}
                      setVariantSelected={setVariantSelected}
                    />
                  ))}
              </>
            </Fragment>
          ))}
        </div>
      </div>
      <div className={footerStyles.questionPageFooter}>
        <Footer
          buttonDisabled={itemsReadyForReturn.length === 0}
          buttonPending={false} // TODO: show pending when loading
          buttonText={
            draftReturn.compensationMethods.length > 1
              ? "Continue to return method"
              : "Continue"
          }
          draftReturn={draftReturn}
          handleClick={() => {
            if (itemsReadyForReturn.length) {
              onComplete?.(draftReturn);
            }
          }}
          hideBranding={settings?.hidePortalBranding}
          returnType={returnType}
          showTooltip
        />
      </div>
      {selectedItem && primaryProduct && (
        <ReturnItemFlow
          draftReturn={draftReturn}
          onCancel={cancelReturnItem}
          onEdit={editReturnItem}
          onReject={onReject}
          onSubmit={addItemToReturn}
          product={primaryProduct}
          returnableItem={selectedItem}
          selectedReturnItem={selectedItem}
          settings={settings}
          setVariantSelected={setVariantSelected}
          variantSelected={variantSelected}
        />
      )}
    </>
  );
});
